import React from 'react';
import { Box, styled, CircularProgress, Typography } from '@mui/material';

export interface WaveformData {
  peaks: number[];
  length: number;
  sampleRate: number;
  channels: number;
  duration: number;
}

interface WaveformVisualizerProps {
  width?: number;
  height?: number;
  color?: string;
  animated?: boolean;
  bars?: number;
  // New props for real waveform data
  waveformData?: WaveformData | null;
  loading?: boolean;
  interactive?: boolean;
  currentTime?: number;
  onSeek?: (time: number) => void;
}

const WaveformContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'interactive',
})<{ interactive?: boolean }>(({ interactive }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1,
  cursor: interactive ? 'pointer' : 'default',
  position: 'relative',
  overflow: 'hidden',
}));

const LoadingContainer = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1,
  color: 'text.secondary',
}));

const ProgressIndicator = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'progress' && prop !== 'progressColor',
})<{ progress: number; progressColor: string }>(({ progress, progressColor }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  height: '100%',
  width: `${progress * 100}%`,
  backgroundColor: progressColor,
  opacity: 0.3,
  pointerEvents: 'none',
  transition: 'width 0.1s ease-out',
}));

const WaveformBar = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'delay' && prop !== 'animated' && prop !== 'barColor',
})<{ delay: number; animated: boolean; barColor: string }>(({ delay, animated, barColor }) => ({
  width: 3,
  backgroundColor: barColor,
  borderRadius: 2,
  transformOrigin: 'bottom',
  animation: animated ? `waveform ${1.5 + delay * 0.1}s ease-in-out infinite alternate` : 'none',
  '@keyframes waveform': {
    '0%': {
      transform: 'scaleY(0.3)',
      opacity: 0.6,
    },
    '100%': {
      transform: 'scaleY(1)',
      opacity: 1,
    },
  },
}));

export const WaveformVisualizer: React.FC<WaveformVisualizerProps> = ({
  width = 60,
  height = 24,
  color = '#FF6B35',
  animated = true,
  bars = 8,
  waveformData,
  loading = false,
  interactive = false,
  currentTime = 0,
  onSeek,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Generate bar heights based on real waveform data or fallback to random
  const barHeights = React.useMemo(() => {
    if (waveformData && waveformData.peaks.length > 0) {
      // Use real waveform data
      const targetBars = Math.min(bars, waveformData.peaks.length);
      const step = Math.floor(waveformData.peaks.length / targetBars);
      
      return Array.from({ length: targetBars }, (_, i) => {
        const startIdx = i * step;
        const endIdx = Math.min(startIdx + step, waveformData.peaks.length);
        
        // Get the maximum peak value in this segment
        let maxPeak = 0;
        for (let j = startIdx; j < endIdx; j++) {
          maxPeak = Math.max(maxPeak, waveformData.peaks[j]);
        }
        
        // Normalize to 0.1-1.0 range for visual clarity
        return Math.max(0.1, maxPeak);
      });
    } else {
      // Fallback to random data for demo/loading state
      return Array.from({ length: bars }, () => Math.random() * 0.7 + 0.3);
    }
  }, [waveformData, bars]);

  // Calculate progress for current time indicator
  const progress = React.useMemo(() => {
    if (!waveformData || waveformData.duration <= 0) return 0;
    return Math.min(1, Math.max(0, currentTime / waveformData.duration));
  }, [currentTime, waveformData]);

  // Handle click for seeking
  const handleClick = React.useCallback((event: React.MouseEvent) => {
    if (!interactive || !onSeek || !containerRef.current || !waveformData) return;

    const rect = containerRef.current.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const clickProgress = clickX / rect.width;
    const seekTime = clickProgress * waveformData.duration;
    
    onSeek(seekTime);
  }, [interactive, onSeek, waveformData]);

  // Show loading state
  if (loading) {
    return (
      <LoadingContainer sx={{ width, height }}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">
          Processing audio...
        </Typography>
      </LoadingContainer>
    );
  }

  return (
    <WaveformContainer 
      ref={containerRef}
      interactive={interactive}
      onClick={handleClick}
      sx={{ width, height }}
      role="img"
      aria-label={
        waveformData 
          ? `Audio waveform visualization - Duration: ${Math.round(waveformData.duration)}s`
          : "Audio waveform visualization"
      }
      aria-hidden={!interactive} // Interactive waveforms should be accessible
    >
      {/* Progress indicator for current playback position */}
      {interactive && waveformData && (
        <ProgressIndicator 
          progress={progress} 
          progressColor={color}
        />
      )}
      
      {/* Waveform bars */}
      {barHeights.map((heightRatio, index) => (
        <WaveformBar
          key={index}
          delay={index}
          animated={animated && !waveformData} // Only animate fallback/demo data
          barColor={color}
          sx={{
            height: height * heightRatio,
            flex: waveformData ? 1 : 'none', // Fill width for real data, fixed width for demo
            maxWidth: waveformData ? undefined : 3,
            // Reduced motion support
            '@media (prefers-reduced-motion: reduce)': {
              animation: 'none',
            },
            // Interactive hover effect
            ...(interactive && {
              '&:hover': {
                opacity: 0.8,
                transform: 'scaleY(1.1)',
                transition: 'all 0.1s ease-out',
              },
            }),
          }}
        />
      ))}
    </WaveformContainer>
  );
};